import React, { useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { useConfig } from "@/lib/useConfig";

const inputCls = "flex-1 bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#F59E0B] focus:outline-none uppercase";

export default function PromoCodeField({ onApplied }) {
  const { promoEnabled } = useConfig();
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null); // {ok:true,msg} | {ok:false,msg}
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(false);

  const apply = async () => {
    const c = code.trim();
    if (!c || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promo/validate", { code: c });
      if (data.valid) {
        setStatus({ ok: true, msg: `Code applied — ${data.discount_pct}% discount added` });
        setApplied(true);
        onApplied({ code: data.code, discount_pct: data.discount_pct });
      } else {
        setStatus({ ok: false, msg: data.error || "Invalid promo code" });
        setApplied(false);
        onApplied(null);
      }
    } catch (err) {
      setStatus({ ok: false, msg: err?.response?.data?.detail || "Invalid promo code" });
      setApplied(false);
      onApplied(null);
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    setCode(""); setStatus(null); setApplied(false); onApplied(null);
  };

  if (!promoEnabled) return null;

  return (
    <div className="md:col-span-2 rounded-lg border border-[#F59E0B]/25 bg-[#F59E0B]/[0.06] p-3.5" data-testid="promo-field">
      <>
      <label className="text-xs font-semibold text-[#FBBF24] mb-1.5 block">Have a promo code?</label>
      <div className="flex gap-2">
        <input
          value={code}
          disabled={applied}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
          placeholder="e.g. LAUNCH50"
          className={inputCls}
          data-testid="promo-input"
        />
        {applied ? (
          <button type="button" onClick={remove} data-testid="promo-remove"
            className="px-4 py-2.5 rounded-md text-sm font-semibold border border-white/15 text-[#94A3B8] hover:text-white transition">
            Remove
          </button>
        ) : (
          <button type="button" onClick={apply} disabled={busy || !code.trim()} data-testid="promo-apply"
            className="px-5 py-2.5 rounded-md text-sm font-semibold bg-[#F59E0B] text-[#3b2402] hover:brightness-110 transition disabled:opacity-50">
            {busy ? "…" : "Apply"}
          </button>
        )}
      </div>
      {status && (
        <p className={`mt-2 text-xs flex items-center gap-1.5 ${status.ok ? "text-[#22C55E]" : "text-[#EF4444]"}`}
          data-testid={status.ok ? "promo-success" : "promo-error"}>
          {status.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {status.msg}
        </p>
      )}
      </>
    </div>
  );
}
